<template>
  <div class="page-table scrollable only-y">
    <div class="no-table-box page-header">
      <h1 class="pb-15">
        <v-svg class="voxo-yellow" sprite="microphone" />
        Music On Holds
      </h1>
      <div class="flex pb-20">
        <div class="box">
          <el-input v-model="search" placeholder="Search" clearable> </el-input>
        </div>
        <div class="box right">
          <el-button @click="openCreateModal">Add New</el-button>
        </div>
      </div>

      <div class="vxe-table-height card-base card-shadow--medium">
        <vxe-grid
          round
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          show-overflow="tooltip"
          highlight-hover-row
          @cell-click="cellClicked"
          @cell-mouseenter="rowHover"
          @cell-mouseleave="rowLeave"
          :data="tableData"
          :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
          :loading="tableLoading"
          :columns="tableColumns"
        />
      </div>

      <create-form
        :createVisible="createVisible"
        :createProcessing="createProcessing"
        :clearFormFields="clearFormFields"
        @cancelOperation="cancelOperation"
        @createOperation="createOperation"
      />

      <edit-form
        :editVisible="editVisible"
        :editProcessing="editProcessing"
        :clearFormFields="clearFormFields"
        :selectedMusicOnHold="selectedMusicOnHold"
        @cancelOperation="cancelOperation"
        @editOperation="editOperation"
      />
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { debounce } from "lodash";
import CreateForm from "./components/createForm";
import EditForm from "./components/editForm";
import { apiV2Host } from "../../../feathers/feathers-client";

export default {
  name: "GlobalMusicOnHoldsTable",
  data() {
    return {
      selectedMusicOnHold: null,
      tableData: [],
      allMOH: [],
      search: "",
      visibleActionsId: "",
      editVisible: false,
      editProcessing: false,
      createVisible: false,
      createProcessing: false,
      clearFormFields: false,
      tableLoading: false,
      tableColumns: [
        {
          field: "sprite",
          title: "",
          width: 50,
          slots: {
            default: ({ row }) => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-yellow" sprite="microphone" />
                </div>,
              ];
            },
          },
        },
        {
          field: "name",
          title: "Name",
          minWidth: 150,
          sortable: true,
        },
        {
          field: "actions",
          title: "",
          minWidth: "150",
          showOverflow: false,
          slots: {
            default: ({ row }) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  <el-button
                    id={"edit-" + row.id}
                    icon="el-icon-edit"
                    onClick={() => this.openEditModal(row.id)}
                    class="table-action-button icon-blue"
                  />
                  <el-button
                    id={"delete-" + row.id}
                    icon="el-icon-delete"
                    onClick={() => this.deleteOperation(row.id)}
                    class="table-action-button icon-red"
                  />
                </div>,
              ];
            },
          },
        },
      ],
    };
  },

  components: {
    CreateForm,
    EditForm,
  },

  computed: {
    user() {
      return this.$store.state.auth.user;
    },
    token() {
      return this.$store.state.auth.accessToken;
    }
  },

  watch: {
    user() {
      this.findRecords();
    },
    search: debounce(function () {
      this.searchMOH();
    }, 500),
  },

  methods: {
    authHeaders() {
      return { Authorization: `Bearer ${this.token}` };
    },

    searchMOH() {
      if (this.search.length) {
        this.tableData = this.allMOH.filter((data) =>
          data.name.toLowerCase().includes(this.search.toLowerCase())
        );
      } else {
        this.tableData = [...this.allMOH];
      }
    },

    openCreateModal() {
      this.createVisible = true;
      this.clearFormFields = false;
    },

    openEditModal(id) {
      axios
        .get(`${apiV2Host}/admin/music-on-hold/${id}`, { headers: this.authHeaders() })
        .then(res => {
          this.selectedMusicOnHold = res.data;
          this.clearFormFields = false;
          this.editVisible = true;
        })
        .catch(() => true);
    },

    cellClicked(data) {
      if (data.column.property !== "actions") {
        this.openEditModal(data.row.id);
      }
    },

    rowHover(data) {
      this.visibleActionsId = data.row.id;
    },

    rowLeave() {
      this.visibleActionsId = "";
    },

    findRecords() {
      this.tableLoading = true;
      axios
        .get(`${apiV2Host}/admin/music-on-hold/system`, {
          params: { recordsPerPage: 500 },
          headers: this.authHeaders()
        })
        .then(res => {
          const records = res.data.records || [];
          this.tableData = [...records];
          this.allMOH = [...records];
          this.tableLoading = false;
        })
        .catch(() => { this.tableLoading = false; });
    },

    // ⭐️ Create Operation ⭐️ //
    createOperation(createBody) {
      this.$confirm("Create music on hold?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      })
        .then(() => {
          this.createProcessing = true;
          axios
            .post(`${apiV2Host}/admin/music-on-hold`, createBody, { headers: this.authHeaders() })
            .then(() => {
              this.$notify({ title: "Success", message: "Created successfully!", type: "success" });
              this.createProcessing = false;
              this.cancelOperation("create");
            })
            .catch(error => {
              this.handleError(error);
              this.createProcessing = false;
            });
        })
        .catch(() => true);
    },

    // ⭐️ Edit Operation ⭐️ //
    editOperation(editBody) {
      this.$confirm("Save music on hold?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      })
        .then(() => {
          this.editProcessing = true;
          axios
            .patch(`${apiV2Host}/admin/music-on-hold/${this.selectedMusicOnHold.id}`, editBody, { headers: this.authHeaders() })
            .then(() => {
              this.$notify({ title: "Success", message: "Edited successfully!", type: "success" });
              this.editProcessing = false;
              this.cancelOperation("edit");
            })
            .catch(error => {
              this.handleError(error);
              this.editProcessing = false;
            });
        })
        .catch(() => true);
    },

    // ⭐️ Delete Operation ⭐️ //
    deleteOperation(id) {
      this.$confirm("Delete music on hold?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      })
        .then(() => {
          this.tableLoading = true;
          axios
            .delete(`${apiV2Host}/admin/music-on-hold/${id}`, { headers: this.authHeaders() })
            .then(() => {
              this.$notify({ title: "Success", message: "Deleted successfully!", type: "success" });
              this.findRecords();
            })
            .catch(error => {
              this.handleError(error);
              this.tableLoading = false;
            });
        })
        .catch(() => true);
    },

    // ⭐️ Cancel Operation ⭐️ //
    cancelOperation(type) {
      if (type === "create") {
        this.createVisible = false;
      } else if (type === "edit") {
        this.editVisible = false;
      }
      this.clearFormFields = true;
      this.findRecords();
    },

    handleError(error) {
      const data = (error.response && error.response.data) || {};
      const message = (data.validationErrors && data.validationErrors[0]) || data.message || "An error occurred";
      this.$notify({ title: data.name || "Error", message, type: "error" });
    }
  },

  mounted() {
    if (this.user) {
      this.findRecords();
    }
  },
};
</script>
